"use strict";
/*jshint esversion: 11 */

import React, {useState, useEffect} from 'react';
import {createRoot} from 'react-dom/client';
import {startLoadingSpinner, stopLoadingSpinner} from '../../csf-ufc-pivotal/includes/utils';
import PivotalLoginApi from "../../../js/csf/pivotal/PivotalLoginApi";
import PropTypes from "prop-types";
import PivotalApiEnvironments
  from "../../../js/csf/pivotal/PivotalApiEnvironments";
import {COOKIE_LOGGED_IN_NAME, setCookie} from "../utils/utils";

export function CsfPivotalMembreLogin({apiUrl, apiEnv}) {

  const [loginName, setLoginName] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setCookie(COOKIE_LOGGED_IN_NAME, false ,1);
  }, []);

  useEffect(() => {
    if (isError) {
      moveErrorOutputToHero();
    }
  }, [isError]);

  const _getFormFieldWrapperClasses = function () {
    return "js-form-item form-item js-form-type-password form-item-pass js-form-item-pass form-group" +
      (isError ? " form-item--error error" : "");
  };

  /**
   * This function handle the submit button of the form. It then call the pivotal api to login
   * @param {Object} event - javascript event object
   */
  function handleSubmit(event) {
    event.preventDefault();
    startLoadingSpinner();
    const PivotalLogin = new PivotalLoginApi(apiUrl, apiEnv, null);
    PivotalLogin.callLoginEndpoint({userName: loginName, password: loginPassword, scope: "full refresh_token"}, PivotalApiEnvironments.getLoginSuffix())
      .then(data => {
        if (data && data.success && data.payload && data.payload.access_token) {
          completeSiteLogin({
            accessToken: data.payload.access_token,
            tokenType: data.payload.token_type,
            tokenExpiresIn: data.payload.expires_in,
            refreshtoken: data.payload.refresh_token,
            envName: data.payload.environmentName,
            username: data.payload.user_name,
          });
        } else {
          handleErrorResponse('Error on the request.');
        }
      })
      .catch(error => {
        handleErrorResponse(error);
      });
  }

  /**
   * This function handle the last step of the login process. Its send login data to the drupal application.
   * @param {Object} loginData - data of the api call from the logged in event
   */
  function completeSiteLogin(loginData) {
    const url = "/csf-api-membre/login-user-to-site";
    const requestMetadata = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pivotalEnvironmentName': apiEnv
      },
      body: JSON.stringify(loginData)
    };
    fetch(url, requestMetadata)
      .then(res => {
        if (!res.ok) {
          throw new Error("HTTP status " + res.status);
        }
        return res;
      })
      .then(res => res.json())
      .then(data => {
        if (data.redirect !== undefined) {
          window.location.href = data.redirect; // success: redirect visitor to default logged-in page
        } else {
          handleErrorResponse('no data.redirect');
        }
      }).catch((error) => {
      handleErrorResponse(error);
    });

  }

  /**
   * This function handle the behavior if there is an error given from the api.
   * @param {String} error - The error string to output.
   */
  function handleErrorResponse(error) {
    console.log(error);
    setIsError(true);
    stopLoadingSpinner();
  }

  /**
   * This function that take the message error output, and its move the html inside the hero banner.
   */
  function moveErrorOutputToHero() {
    const errormessage = document.querySelector('.user-login-form .user-form-error-membre');
    if (errormessage) {
      document.querySelector('.messagebox').classList.add('messagebox-error');
      document.querySelector('.messagebox .messagebox-text').innerHTML = errormessage.innerHTML;
      document.querySelector('.messagebox').classList.remove('d-none');
      errormessage.remove();
    }
  }

  return (
    <form className="user-login-form" autoComplete="off" onSubmit={handleSubmit}>

      {isError && (
        <div className="user-form-error-membre">
          <div className="grid-container-large">
            <div className="d-flex flex-column flex-lg-row align-items-center">
              <p
                dangerouslySetInnerHTML={{__html: window.Drupal.t('broker.login.loginfailed.message')}}>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={_getFormFieldWrapperClasses()}>

        <label htmlFor="loginFormUsernameInput" className="js-form-required form-required">
          {isError ? (
            window.Drupal.t('membre.login.username.label.error')
          ) : (
            window.Drupal.t('membre.login.username.label')
          )}
        </label>
        <input autoCorrect="none"
               autoCapitalize="none"
               spellCheck="false"
               type="text"
               id="loginFormUsernameInput"
               name="loginFormUsernameInput"
               value={loginName}
               size="15" maxLength="60"
               className="form-text required" required="required"
               aria-required="true"
               onChange={e => setLoginName(e.target.value)}
               onInvalid={e => e.target.setCustomValidity(window.Drupal.t('broker.field.validation.message'))}
               onInput={e => e.target.setCustomValidity('')} />

      </div>

      <div className={_getFormFieldWrapperClasses()}>

        <label htmlFor="loginFormPasswordInput" className="js-form-required form-required">
          {isError ? (
            window.Drupal.t('membre.login.password.label.error')
          ) : (
            window.Drupal.t('membre.login.password.label')
          )}
        </label>
        <div className="field__password">
          <input data-drupal-selector="edit-pass"
                 type="password"
                 id="loginFormPasswordInput" name="loginFormPasswordInput"
                 size="15"
                 maxLength="128"
                 value={loginPassword}
                 className="form-text required"
                 required="required"
                 aria-required="true"
                 onChange={e => setLoginPassword(e.target.value)}
                 onInvalid={e => e.target.setCustomValidity(window.Drupal.t('broker.field.validation.message'))}
                 onInput={e => e.target.setCustomValidity('')}/>

          <a className="togglepass">
            <span className="passIcon hidePass">
              <svg xmlns="http://www.w3.org/2000/svg"
                   viewBox="0 0 576 512">{/* Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. */}
                <path
                  d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM432 256c0 79.5-64.5 144-144 144s-144-64.5-144-144s64.5-144 144-144s144 64.5 144 144zM288 192c0 35.3-28.7 64-64 64c-11.5 0-22.3-3-31.6-8.4c-.2 2.8-.4 5.5-.4 8.4c0 53 43 96 96 96s96-43 96-96s-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6z"></path>
              </svg>
            </span>
            <span className="passIcon showPass" style={{display: 'none'}}>
              <svg xmlns="http://www.w3.org/2000/svg"
                   viewBox="0 0 640 512">{/* Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. */}
                <path
                  d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c5.2-11.8 8-24.8 8-38.5c0-53-43-96-96-96c-2.8 0-5.6 .1-8.4 .4c5.3 9.3 8.4 20.1 8.4 31.6c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zm223.1 298L373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5z"></path>
              </svg>
            </span>
          </a>
        </div>
      </div>

      <br/>

      <a href={window.Drupal.t('member.resetPasswordUrl')}
         title={window.Drupal.t('Send password reset instructions via email.')}
         className='request-password-link'>
        {window.Drupal.t('Reset your password')}
      </a>

      <div data-drupal-selector="edit-actions" className="form-actions js-form-wrapper form-wrapper" id="edit-actions">
        <input type="submit" name="op" value={window.Drupal.t('Log in')}
               className="button js-form-submit form-submit btn-primary btn"/>
      </div>
    </form>
  );
}

window.addEventListener('DOMContentLoaded', () => {
  const domNode = document.getElementById('CSFPivotalMembreLogin');
  if (domNode !== null) {
    const root = createRoot(domNode);

    const apiUrl = domNode.getAttribute("data-api-url");
    const apiEnv = domNode.getAttribute("data-api-env");

    root.render(<CsfPivotalMembreLogin
      apiUrl={apiUrl}
      apiEnv={apiEnv}
    />);
  }
});


CsfPivotalMembreLogin.propTypes = {
  apiUrl: PropTypes.string,
  apiEnv: PropTypes.string,
};
